// ────────────────────────────────────────────────────────────────────────────
// Compact inspector controls for the Document Builder.
//
// The inspector column is ~340px wide and packs a lot of options, so these are
// tighter than the shared `@my/ui` form primitives (which are sized for
// full-width settings forms). Labels sit inline on the left, control on the
// right, one row per option.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { AlignCenter, AlignLeft, AlignRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { moveItem } from '../lib/arrays';
import type { Align } from '../lib/types';

/** Inline `label ─ control` row. */
export function InspectorRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0">
        <p className="truncate text-xs text-slate-600">{label}</p>
        {hint && <p className="truncate text-[10px] text-slate-400">{hint}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1">{children}</div>
    </div>
  );
}

/** Section heading inside the inspector. */
export function InspectorGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </p>
      {children}
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-2 text-xs',
        disabled ? 'cursor-not-allowed text-slate-300' : 'cursor-pointer text-slate-700',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
      />
      <span className="truncate">{label}</span>
    </label>
  );
}

export function TextField({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-7 rounded border border-slate-200 px-2 text-xs text-slate-800 focus:border-sky-400 focus:outline-none',
        className ?? 'w-40',
      )}
    />
  );
}

export function NumberField({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  suffix,
}: {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const next = Number(e.target.value);
          // An empty input parses as NaN — keep the last good value rather than
          // writing NaN into the layout and blanking the preview.
          if (!Number.isNaN(next)) onChange(Math.min(max, Math.max(min, next)));
        }}
        className="h-7 w-16 rounded border border-slate-200 px-1.5 text-right text-xs tabular-nums text-slate-800 focus:border-sky-400 focus:outline-none"
      />
      {suffix && <span className="text-[10px] text-slate-400">{suffix}</span>}
    </div>
  );
}

export function SelectField<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="h-7 rounded border border-slate-200 bg-white px-1.5 text-xs text-slate-800 focus:border-sky-400 focus:outline-none"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

const ALIGN_ICON = { left: AlignLeft, center: AlignCenter, right: AlignRight } as const;

export function AlignPicker({
  value,
  onChange,
  options = ['left', 'center', 'right'],
}: {
  value: Align;
  onChange: (next: Align) => void;
  options?: Align[];
}) {
  return (
    <div className="flex rounded border border-slate-200 p-0.5">
      {options.map((align) => {
        const Icon = ALIGN_ICON[align];
        const active = value === align;
        return (
          <button
            key={align}
            type="button"
            aria-label={`Align ${align}`}
            title={`Align ${align}`}
            onClick={() => onChange(align)}
            className={cn(
              'rounded p-1 transition-colors',
              active ? 'bg-slate-100 text-slate-700' : 'text-slate-400 hover:text-slate-700',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

/** Native colour picker plus the hex, because designers paste hex codes. */
export function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-7 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-20 rounded border border-slate-200 px-1.5 font-mono text-[11px] uppercase text-slate-700 focus:border-sky-400 focus:outline-none"
      />
    </div>
  );
}

/** Up / down pair used by every reorderable list in the inspector. */
export function MoveButtons({
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-label="Move up"
        disabled={!canUp}
        onClick={onUp}
        className="text-slate-300 transition-colors enabled:hover:text-slate-600 disabled:opacity-40"
      >
        <ChevronUp className="h-3 w-3" />
      </button>
      <button
        type="button"
        aria-label="Move down"
        disabled={!canDown}
        onClick={onDown}
        className="text-slate-300 transition-colors enabled:hover:text-slate-600 disabled:opacity-40"
      >
        <ChevronDown className="h-3 w-3" />
      </button>
    </div>
  );
}

/**
 * Ordered pick-list: selected keys first (in print order, reorderable), then
 * the remaining options. Ticking appends to the end of the selection, which is
 * what the operator expects — the new field shows up at the bottom of the
 * block, not somewhere in the middle.
 */
export function FieldPicker<T extends string>({
  all,
  selected,
  labels,
  onChange,
}: {
  all: readonly T[];
  selected: T[];
  labels: Record<T, string>;
  onChange: (next: T[]) => void;
}) {
  const unselected = all.filter((key) => !selected.includes(key));
  return (
    <div className="space-y-0.5">
      {selected.map((key, i) => (
        <div key={key} className="flex items-center justify-between gap-2 rounded px-1 hover:bg-slate-50">
          <Checkbox
            checked
            label={labels[key]}
            onChange={() => onChange(selected.filter((k) => k !== key))}
          />
          <MoveButtons
            canUp={i > 0}
            canDown={i < selected.length - 1}
            onUp={() => onChange(moveItem(selected, i, -1))}
            onDown={() => onChange(moveItem(selected, i, 1))}
          />
        </div>
      ))}
      {unselected.map((key) => (
        <div key={key} className="px-1">
          <Checkbox
            checked={false}
            label={labels[key]}
            onChange={() => onChange([...selected, key])}
          />
        </div>
      ))}
    </div>
  );
}
